/**
 * Interval polling, done safely.
 *
 * The legacy app started `setInterval` loops from at least six places
 * (`_patientLivePollInterval` at 2s, `_patientSyncInterval` at 5s, the smart
 * refresh background tick, the voucher live sync, the dose countdown ticker and
 * the alarm scanner) and never reliably cleared them — `logout` resorted to
 * looping over every possible interval id and clearing all of them.
 *
 * These hooks clean up on unmount, pause while the tab is hidden (the legacy
 * `visibilitychange` handler did this for one of the six), and never overlap two
 * runs of the same async job.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Call `callback` every `intervalMs`.
 *
 * @param {Function} callback
 * @param {number|null} intervalMs  `null` pauses the poll.
 * @param {object} [options]
 * @param {boolean} [options.immediate=false]      Fire once right away.
 * @param {boolean} [options.pauseWhenHidden=true] Stop while the tab is backgrounded.
 */
export function useInterval(callback, intervalMs, options = {}) {
  const { immediate = false, pauseWhenHidden = true } = options;
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (intervalMs === null || intervalMs === undefined) return undefined;

    let timer = null;

    const tick = () => savedCallback.current();

    const start = () => {
      if (timer !== null) return;
      timer = setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    if (immediate) tick();
    if (!pauseWhenHidden || !document.hidden) start();

    const onVisibilityChange = () => {
      if (!pauseWhenHidden) return;
      if (document.hidden) {
        stop();
      } else {
        tick(); // catch up immediately on return, then resume
        start();
      }
    };

    if (pauseWhenHidden) document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      if (pauseWhenHidden) document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, immediate, pauseWhenHidden]);
}

/**
 * Poll an async fetcher and expose its latest result.
 *
 * A run that is still in flight when the next tick arrives suppresses that tick,
 * so a slow endpoint cannot pile up requests.
 *
 * @param {Function} fetcher
 * @param {number|null} intervalMs
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {Function} [options.onData]
 * @param {Function} [options.onError]
 */
export function usePoll(fetcher, intervalMs, options = {}) {
  const { enabled = true, onData, onError, immediate = true, pauseWhenHidden = true } = options;

  const inFlight = useRef(false);
  const fetcherRef = useRef(fetcher);
  const onDataRef = useRef(onData);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    fetcherRef.current = fetcher;
    onDataRef.current = onData;
    onErrorRef.current = onError;
  }, [fetcher, onData, onError]);

  useInterval(
    async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const result = await fetcherRef.current();
        onDataRef.current?.(result);
      } catch (err) {
        // A poll failure is expected (server restart, flaky wifi) and must not
        // surface as a user-facing error; it is logged and the next tick retries.
        console.warn('[Poll] tick failed:', err.message);
        onErrorRef.current?.(err);
      } finally {
        inFlight.current = false;
      }
    },
    enabled ? intervalMs : null,
    { immediate, pauseWhenHidden },
  );
}

/**
 * A 1Hz clock for live countdowns (dose timers, meal timers).
 *
 * Returns a `Date` that is replaced on every tick, so components reading it
 * re-render. Pass `enabled: false` to freeze it when nothing on screen needs it.
 */
export function useTicker(enabled = true, intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useInterval(() => setNow(new Date()), enabled ? intervalMs : null, { pauseWhenHidden: true });
  return now;
}
